import React from 'react';

import { ColumnDef } from '@tanstack/react-table';
import { Offer } from 'common/contracts';
import { OfferType } from 'constants/enums';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import DialogModal from 'components/DialogModal/DialogModal';
import EditOfferDesignModal from 'components/EditOfferDesignModal/EditOfferDesignModal';
import PortalHeader from 'components/PortalHeader/PortalHeader';

import { OfferTableContent } from './OfferTableContent';
import { OfferTableFilters } from './OfferTableFilters';

interface IOfferPageLayoutProps {
  title: string;
  buttonText: string;
  onCreateClick: () => void;
  canUserEdit: boolean;
  canManageOffers: boolean;
  // Filters
  offerLabel: string;
  showFilters: boolean;
  externalIdFilter: string;
  onExternalIdFilterChange: (value: string) => void;
  nameFilter: string;
  onNameFilterChange: (value: string) => void;
  // Table
  offers: Offer[];
  displayData: Offer[];
  columns: ColumnDef<Offer>[];
  totalCount: number;
  totalPages: number;
  rowsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  emptyStateTitle: string;
  emptyStateDescription: string | React.ReactNode;
  createButtonPrefix: string;
  bodyDataTestId: string;
  emptyStateIcon?: React.ReactNode;
  // Edit Design Modal
  offerType: OfferType;
  offerDesignType: OfferDesignTypes;
  selectedOffer: string | null;
  showLimitedEditModal: boolean;
  onCloseLimitedEditModal: () => void;
  // Delete Modal
  isDeleteDialogOpen: boolean;
  onCloseDeleteDialog: () => void;
  onConfirmDelete: () => void;
  deleteModalTitle: string;
  deleteModalDescription: string;
  // Extra content (e.g. Cheats dialog for Rolling Offers)
  children?: React.ReactNode;
}

export const OfferPageLayout = ({
  title,
  buttonText,
  onCreateClick,
  canUserEdit,
  canManageOffers,
  offerLabel,
  showFilters,
  externalIdFilter,
  onExternalIdFilterChange,
  nameFilter,
  onNameFilterChange,
  offers,
  displayData,
  columns,
  totalCount,
  totalPages,
  rowsPerPage,
  currentPage,
  onPageChange,
  isLoading,
  emptyStateTitle,
  emptyStateDescription,
  createButtonPrefix,
  bodyDataTestId,
  emptyStateIcon,
  offerType,
  offerDesignType,
  selectedOffer,
  showLimitedEditModal,
  onCloseLimitedEditModal,
  isDeleteDialogOpen,
  onCloseDeleteDialog,
  onConfirmDelete,
  deleteModalTitle,
  deleteModalDescription,
  children
}: IOfferPageLayoutProps): React.ReactElement => {
  return (
    <div className="overflow-y-scroll">
      <PortalHeader
        title={title}
        primaryButtonText={buttonText}
        onPrimaryButtonClick={onCreateClick}
        primaryButtonState={{
          disabled: !canUserEdit,
          hidden: !canManageOffers
        }}
        isCreateButton
      />
      {showFilters && (
        <OfferTableFilters
          offerLabel={offerLabel}
          externalIdFilter={externalIdFilter}
          onExternalIdFilterChange={onExternalIdFilterChange}
          nameFilter={nameFilter}
          onNameFilterChange={onNameFilterChange}
        />
      )}
      <OfferTableContent
        offers={offers}
        displayData={displayData}
        columns={columns}
        totalCount={totalCount}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        currentPage={currentPage}
        onPageChange={onPageChange}
        isLoading={isLoading}
        onCreateClick={onCreateClick}
        canCreate={canManageOffers}
        emptyStateTitle={emptyStateTitle}
        emptyStateDescription={emptyStateDescription}
        createButtonPrefix={createButtonPrefix}
        bodyDataTestId={bodyDataTestId}
        emptyStateIcon={emptyStateIcon}
      />
      {showLimitedEditModal && (
        <EditOfferDesignModal
          offerId={selectedOffer || ''}
          offerDesignType={offerDesignType}
          onClose={onCloseLimitedEditModal}
          isOpen={showLimitedEditModal}
          offerType={offerType}
        />
      )}
      {isDeleteDialogOpen && (
        <DialogModal
          isOpen={isDeleteDialogOpen}
          title={deleteModalTitle}
          description={deleteModalDescription}
          modalActions={{
            onConfirm: { label: 'Delete', action: onConfirmDelete },
            onClose: {
              label: 'Cancel',
              action: onCloseDeleteDialog,
              variant: 'link'
            }
          }}
        />
      )}
      {children}
    </div>
  );
};
